import {
  IChatNotificationSettings,
  INotificationSettings,
} from "@home-health-notify/domain";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { AccordionDetails } from "@mui/material";
import {
  debounce,
  NOTIFICATIONS_OPTIONS,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { SettingsItemLabel } from "../UserSettings.style";
import InputRadioGroup from "./inputFields/InputRadioGroup";
import {
  NotificationSettingsItem,
  NotificationsAccordionSummary,
  RadioGroupLabel,
} from "./NotificationSettings.style";

const NOTIFICATION_SAVE_DEBOUNCE_MS = 500;

const EMPTY_GROUP: IChatNotificationSettings = {
  all: false,
  important: false,
  none: false,
};

const EMPTY_NOTIFICATION: INotificationSettings = {
  patientGroup: EMPTY_GROUP,
  nonPatientGroup: EMPTY_GROUP,
  privateChat: EMPTY_GROUP,
};

const NotificationSettings = () => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "settings" });

  const { selectedOrgUser } = useContext(OrganizationContext);
  const organizationId = selectedOrgUser?.getOrganization().organizationId;

  const [notification, setNotification] = useState<INotificationSettings>(
    () =>
      selectedOrgUser?.getOrganization().userProfile.settings.notification ??
      EMPTY_NOTIFICATION,
  );

  const [expanded, setExpanded] = useState<boolean>(false);

  // Re-seed local state when the active organization changes.
  useEffect(() => {
    setNotification(
      selectedOrgUser?.getOrganization().userProfile.settings.notification ??
        EMPTY_NOTIFICATION,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const notificationRef = useRef(notification);
  notificationRef.current = notification;

  const debouncedSaveByGroup = useMemo(() => {
    const createSaver = () =>
      debounce(() => {
        selectedOrgUser?.updateNotificationSettings(notificationRef.current);
      }, NOTIFICATION_SAVE_DEBOUNCE_MS);

    return {
      patientGroup: createSaver(),
      nonPatientGroup: createSaver(),
      privateChat: createSaver(),
    };
  }, [selectedOrgUser]);

  const selectedRadioOption = (settings: IChatNotificationSettings) => {
    const found = NOTIFICATIONS_OPTIONS.find(
      o => settings[o.value as keyof IChatNotificationSettings],
    );
    return found ? found.value : "all";
  };

  const chatNotificationSettings = (
    value: string,
  ): IChatNotificationSettings => ({
    ...EMPTY_GROUP,
    [value]: true,
  });

  const updateNotificationSetting = (
    group: keyof INotificationSettings,
    selectedValue: string,
  ) => {
    setNotification(prev => ({
      ...prev,
      [group]: chatNotificationSettings(selectedValue),
    }));
    debouncedSaveByGroup[group]();
  };

  return (
    <>
      {selectedOrgUser && (
        <NotificationSettingsItem expanded={expanded}>
          <NotificationsAccordionSummary
            expandIcon={<ExpandMoreIcon />}
            onClick={() => setExpanded(!expanded)}
          >
            <NotificationsIcon color="secondary" />
            <SettingsItemLabel>
              {tNs("userSettings.notifications")}
            </SettingsItemLabel>
          </NotificationsAccordionSummary>
          <AccordionDetails>
            <RadioGroupLabel>{tNs("notifications.general")}</RadioGroupLabel>
            <InputRadioGroup
              label={tNs("notifications.patientGroups")}
              value={selectedRadioOption(notification.patientGroup)}
              options={NOTIFICATIONS_OPTIONS}
              onChange={option =>
                updateNotificationSetting("patientGroup", String(option.value))
              }
            />
            <InputRadioGroup
              label={tNs("notifications.internalGroups")}
              value={selectedRadioOption(notification.nonPatientGroup)}
              options={NOTIFICATIONS_OPTIONS}
              onChange={option =>
                updateNotificationSetting(
                  "nonPatientGroup",
                  String(option.value),
                )
              }
            />
            <InputRadioGroup
              label={tNs("notifications.directMessages")}
              value={selectedRadioOption(notification.privateChat)}
              options={NOTIFICATIONS_OPTIONS}
              onChange={option =>
                updateNotificationSetting("privateChat", String(option.value))
              }
            />
          </AccordionDetails>
        </NotificationSettingsItem>
      )}
    </>
  );
};

export default NotificationSettings;
